import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from '../../lib/i18n';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import ClubRouter from '../../lib/routing';
import { getClubTeam } from '../../store/clubs/selectors';
import { isLoadingStream } from '../../store/activity-stream/selectors';
import { fetchAlbumListPageData } from '../../store/media/albums/actions';
import {
  getAlbumList,
  hasLoadedAlbumList,
  getAlbumListPagination,
  getAlbumListPageLoadingError,
} from '../../store/media/albums/selectors';
import { fetchVideoListPageData } from '../../store/media/videos/actions';
import {
  getVideoList,
  hasLoadedVideoList,
  getVideoListPagination,
  getVideoListPageLoadingError,
} from '../../store/media/videos/selectors';
import MediaList from '../../components/media/list';
import Club404 from '../../components/errors/club-404';
import PageDataLoadError from '../../components/errors/page-data-load-error';

class MediaListPage extends React.Component {
  static async getInitialProps({ asPath, club, query, isServer, reduxStore, req, res }) {
    const videosRegex = /videos(\/team-[0-9]+)?\/?/;
    const mediaType = videosRegex.exec(asPath) ? 'videos' : 'albums';
    const { search = '' } = query;
    const page = query.page ? Math.max(parseInt(query.page, 10), 1) : 1;
    const teamId = query.teamId ? parseInt(query.teamId, 10) : 0;

    const initialProps = {
      activeSection: mediaType,
      mediaType,
      namespacesRequired: ['common', 'media'],
      page,
      pageType: `media.list.${mediaType}`,
      search,
      teamId,
    };

    if (!isServer || !club) {
      return initialProps;
    }

    if (!club.galleryEnabled) {
      res.statusCode = 404;
      return initialProps;
    }

    try {
      const loadPageData = mediaType === 'albums' ? fetchAlbumListPageData : fetchVideoListPageData;
      const { error } = await reduxStore.dispatch(loadPageData(club.id, teamId, page, search, req));
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (error) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { mediaType, teamId, page, search } = this.props;
    const {
      mediaType: prevMediaType,
      teamId: prevTeamId,
      page: prevPage,
      search: prevSearch,
    } = prevProps;
    if (
      mediaType !== prevMediaType ||
      teamId !== prevTeamId ||
      page !== prevPage ||
      search !== prevSearch
    ) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      isLoading,
      previouslyLoaded,
      club: { galleryEnabled },
    } = this.props;
    if (galleryEnabled && !isLoading && !previouslyLoaded) {
      const {
        loadAlbums,
        loadVideos,
        mediaType,
        club: { id: clubId },
        teamId,
        page,
        search,
      } = this.props;
      const loadPageData = mediaType === 'albums' ? loadAlbums : loadVideos;
      loadPageData(clubId, teamId, page, search);
    }
  };

  onChangeTeamFilterHandler = (teamId) => {
    const { mediaType, club } = this.props;
    if (teamId) {
      ClubRouter.pushClubRoute(club, `media.${mediaType}.team`, { teamId });
    } else {
      ClubRouter.pushClubRoute(club, `media.${mediaType}`);
    }
  };

  onSearchChangeHandler = (search) => {
    const { mediaType, club, teamId } = this.props;
    let route = `media.${mediaType}`;
    const params = {};
    if (search.length) {
      params.search = search.replace(/[^0-9a-z\-_ ]/gi, '');
    }

    if (teamId) {
      params.teamId = teamId;
      route = `media.${mediaType}.team`;
    }

    ClubRouter.pushClubRoute(club, route, params);
  };

  render() {
    const { loadingError, club } = this.props;

    if (!club.galleryEnabled) {
      return <Club404 club={club} />;
    }

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const { mediaType, isLoading, messages, page, pagination, search, team, t } = this.props;
    return (
      <MediaList
        club={club}
        isLoading={isLoading}
        messages={messages}
        mediaType={mediaType}
        onChangeTeamFilter={this.onChangeTeamFilterHandler}
        onSearchChange={this.onSearchChangeHandler}
        page={page}
        pagination={pagination}
        search={search}
        team={team}
        t={t}
      />
    );
  }
}

MediaListPage.defaultProps = {
  loadingError: null,
  messages: [],
  page: 1,
  pagination: null,
  team: null,
  teamId: null,
};

MediaListPage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadAlbums: PropTypes.func.isRequired,
  loadVideos: PropTypes.func.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  mediaType: PropTypes.oneOf(['albums', 'videos']).isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape()),
  page: PropTypes.number,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    perPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalResults: PropTypes.number.isRequired,
  }),
  previouslyLoaded: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  team: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }),
  teamId: PropTypes.number,
  t: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  loadAlbums: fetchAlbumListPageData,
  loadVideos: fetchVideoListPageData,
};

const mapStateToProps = (state, ownProps) => ({
  isLoading: isLoadingStream(state),
  loadingError:
    ownProps.mediaType === 'albums'
      ? getAlbumListPageLoadingError(state, ownProps.teamId, ownProps.page, ownProps.search)
      : getVideoListPageLoadingError(state, ownProps.teamId, ownProps.page, ownProps.search),
  messages:
    ownProps.mediaType === 'albums'
      ? getAlbumList(state, ownProps.teamId, ownProps.page, ownProps.search)
      : getVideoList(state, ownProps.teamId, ownProps.page, ownProps.search),
  previouslyLoaded:
    ownProps.mediaType === 'albums'
      ? hasLoadedAlbumList(state, ownProps.club.id, ownProps.teamId, ownProps.page, ownProps.search)
      : hasLoadedVideoList(
          state,
          ownProps.club.id,
          ownProps.teamId,
          ownProps.page,
          ownProps.search,
        ),
  pagination:
    ownProps.mediaType === 'albums'
      ? getAlbumListPagination(state, ownProps.teamId, ownProps.search)
      : getVideoListPagination(state, ownProps.teamId, ownProps.search),
  team: ownProps.teamId ? getClubTeam(state, ownProps.club.id, ownProps.teamId) : null,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation(['common', 'media'])(MediaListPage));
